import React, { useState } from 'react';
import { Download, Image as ImageIcon, FileText, Share2, ClipboardList, Save, Check } from 'lucide-react';
import { copyText, downloadFile, copyHtml, shareText, HISTORY_KEY } from '../../lib/helpers';
import ImageModal from './ImageModal';

export default function ResultsPanel({ t, text, size, styles, activeStyle, output, history, setHistory, onCopy }) {
  const [showExport, setShowExport] = useState(false);
  const [imgOpen, setImgOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const allText = () => styles.map((s) => `${s.name}: ${s.fn(text)}`).join('\n');

  const copyRow = async (s) => {
    if (!text) return;
    if (!await onCopy(s.fn(text), s.name)) return;
    setCopiedId(s.id);
    setTimeout(() => setCopiedId(null), 1400);
  };

  return (
    <>
      <div className="tool-card overflow-hidden" data-testid="all-results">
        <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 bg-[var(--paper2)]">
          <span className="text-[13px] font-semibold text-[var(--ink)]">{t.all} <span className="font-normal text-[var(--ink4)] text-[12px]">({styles.length})</span></span>
          <div className="flex gap-2">
            <button className="btn-outline" onClick={() => text && copyText(allText(), t.copied)} data-testid="copy-all-btn">{t.copyAll}</button>
            <button className="btn-gold inline-flex items-center gap-1.5" onClick={() => setShowExport(!showExport)} data-testid="save-export-btn"><Download className="w-3.5 h-3.5" />{t.save}</button>
          </div>
        </div>
        {styles.map((s) => (
          <div key={s.id} className="rrow" data-testid={`result-row-${s.id}`}>
            <div className="min-w-0">
              <div className="upper-label !text-[9.5px]">{s.name}</div>
              <div className="text-[var(--ink)] mt-1 break-all" style={{ fontSize: size }}>{text ? s.fn(text) : '…'}</div>
            </div>
            <button className={`copy-btn shrink-0 inline-flex items-center gap-1 ${copiedId === s.id ? 'done' : ''}`} onClick={() => copyRow(s)} data-testid={`copy-${s.id}`}>
              {copiedId === s.id ? <><Check className="w-3 h-3" />{t.copied.replace('!', '')}</> : t.copy}
            </button>
          </div>
        ))}
      </div>

      {showExport && (
        <div className="tool-card cream px-3.5 py-3.5 fade-up" data-testid="export-card">
          <div className="upper-label flex items-center gap-1.5 mb-3"><Save className="w-3.5 h-3.5 text-[var(--gold2)]" />{t.saveTitle}</div>
          <div className="flex flex-wrap gap-2">
            <button className="btn-gold inline-flex items-center gap-1.5" onClick={() => output && downloadFile('cursive-text.txt', output)} data-testid="dl-txt"><Download className="w-3.5 h-3.5" />{t.dlTxt}</button>
            <button className="btn-outline inline-flex items-center gap-1.5" onClick={() => text && downloadFile('all-cursive-styles.txt', allText())} data-testid="dl-all"><FileText className="w-3.5 h-3.5" />{t.dlAll}</button>
            <button className="btn-outline inline-flex items-center gap-1.5" onClick={() => setImgOpen(true)} data-testid="dl-image"><ImageIcon className="w-3.5 h-3.5" />{t.dlImg}</button>
            <button className="btn-outline inline-flex items-center gap-1.5" onClick={() => output && copyHtml(output)} data-testid="copy-word"><ClipboardList className="w-3.5 h-3.5" />{t.word}</button>
            <button className="btn-outline inline-flex items-center gap-1.5" onClick={() => shareText(output || '')} data-testid="share-btn"><Share2 className="w-3.5 h-3.5" />{t.share}</button>
          </div>
        </div>
      )}

      <div className="tool-card px-3.5 py-3.5" data-testid="history">
        <div className="flex items-center justify-between mb-2.5">
          <span className="upper-label flex items-center gap-2"><span className="w-4 h-px bg-[var(--gold2)]" />{t.recent}</span>
          <button className="sm-btn" onClick={() => { localStorage.removeItem(HISTORY_KEY); setHistory([]); }} data-testid="clear-history">{t.clear}</button>
        </div>
        {history.length === 0 ? (
          <div className="text-[12.5px] text-[var(--ink4)] italic py-2">{t.empty}</div>
        ) : (
          <div className="space-y-1.5">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between gap-3 bg-white border border-[var(--line)] rounded-md px-3 py-2" data-testid={`history-item-${i}`}>
                <div className="min-w-0">
                  <div className="text-[14px] text-[var(--ink)] truncate">{h.text}</div>
                  <div className="text-[10px] text-[var(--ink4)]">{h.style}</div>
                </div>
                <button className="copy-btn shrink-0" onClick={() => copyText(h.text, t.copied)}>{t.copy}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ImageModal open={imgOpen} onOpenChange={setImgOpen} text={output || activeStyle.fn(t.sample)} />
    </>
  );
}
